import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Users, Briefcase, ArrowRight } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Full-screen transition played while the app switches between Client and
 * Helper mode. Masks the route change with a branded morph — the two role
 * icons cross-fade toward the destination — so the switch reads as an
 * intentional, lively moment rather than an abrupt page swap.
 *
 * Controlled: pass the destination `target` to show it, `null` to hide.
 * The caller sequences navigation to happen mid-animation (see Header).
 * Respects prefers-reduced-motion (quiet fade, no sliding icons).
 */
export function ModeSwitchTransition({ target }: { target: 'helper' | 'client' | null }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const toHelper = target === 'helper';

  return (
    <AnimatePresence>
      {target && (
        <motion.div
          className="fixed inset-0 z-[120] flex flex-col items-center justify-center"
          style={{
            background:
              'radial-gradient(120% 120% at 50% 0%, hsl(var(--kanfy-teal)) 0%, hsl(var(--kanfy-ink)) 55%, hsl(var(--kanfy-ink)) 100%)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.2 : 0.35, ease: 'easeOut' }}
          role="status"
          aria-live="assertive"
          aria-label={toHelper ? t('a11ySwitchingToHelperMode') : t('a11ySwitchingToClientMode')}
        >
          {/* Icon morph: source role slides out, destination role settles in */}
          <div className="flex items-center gap-5 text-white/90">
            <motion.div
              className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
              initial={reduce ? { opacity: 0 } : { opacity: 0.4, scale: 0.9 }}
              animate={reduce ? { opacity: 0.7 } : { opacity: 0.55, scale: 0.92 }}
              transition={{ duration: 0.4 }}
            >
              {toHelper ? <Users className="h-8 w-8" /> : <Briefcase className="h-8 w-8" />}
            </motion.div>

            <motion.div
              initial={reduce ? { opacity: 0 } : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <ArrowRight className="h-6 w-6 text-white/70" />
            </motion.div>

            <motion.div
              className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white text-[color:hsl(var(--kanfy-ink))] shadow-xl"
              initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.7, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={reduce ? { duration: 0.3 } : { type: 'spring', stiffness: 260, damping: 18, delay: 0.15 }}
            >
              {toHelper ? <Briefcase className="h-10 w-10" /> : <Users className="h-10 w-10" />}
            </motion.div>
          </div>

          <motion.p
            className="mt-7 text-lg font-semibold text-white"
            initial={{ opacity: 0, y: reduce ? 0 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          >
            Switching to {toHelper ? 'Helper' : 'Client'} mode
          </motion.p>

          {/* Three bouncing dots */}
          <div className="mt-3 flex gap-1.5" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="h-2 w-2 rounded-full bg-white/70"
                animate={reduce ? { opacity: 0.7 } : { y: [0, -6, 0], opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
